import { Component, Input, Output, input, signal, inject, ViewChild, EventEmitter } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DeviceDetectService, ToolsService, I18nService } from '@app/core/services';
import { NotificationsService } from '@app/core/services/notifications/notifications.service';
import { CustomValidators } from '@app/core/validators/custom-validators';
import { AutocompleteForFiltersItem } from '@app/shared/forms/autocomplete-for-filters/autocomplete-for-filters-item.interface';
import { ViewService } from '@makiwin/ngx-drupal8-rest';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Subject, Observable, map, tap, debounceTime, switchMap } from 'rxjs';
import { StudentDetails } from './student-details.interface';
import { ResponsiveImageResource } from '@app/core/models';
import { ConfirmModalResult, ModalManagerService } from '@app/core/services/modal-manager';

interface StudentItem {
  uid: string;
  name: string;
  field_first_name: string;
  field_surname: string;
  user_picture_multiple_json: ResponsiveImageResource;
  field_address_country_code: string;
  mail: string;
  preferred_langcode: {
    code: string;
    name: string;
  };
}

/**
 * The `StudentDetailsComponent` is an Angular component that displays and manages student details.
 * It allows for viewing, modifying, and searching for student information.
 *
 *
 * @example
 * ```html
 * <certification-form-student-details
 *   [isModify]="true"
 *   [readOnly]="false"
 *   [registerMode]="true"
 *   [userDetails]="studentDetails"
 *   (userDetailsChange)="onUserDetailsChange($event)">
 * </certification-form-student-details>
 * ```
 */
@Component({
  selector: 'certification-form-student-details',
  templateUrl: './student-details.component.html',
  styleUrls: ['./student-details.component.scss'],
})
export class StudentDetailsComponent {
  /**
   * Indicates whether the component is in modify mode.
   * When set to `true`, the component allows modifications.
   * Defaults to `false`.
   */
  @Input() isModify = false;

  /**
   * Indicates whether the student details component should be in read-only mode.
   * When set to `true`, the component will not allow any modifications to the student details.
   * Defaults to `false`.
   */
  @Input() readOnly = false;

  /**
   * Event emitter that emits when the user details change.
   * This event provides the updated `StudentDetails` object.
   */
  @Output() userDetailsChange: EventEmitter<StudentDetails> = new EventEmitter<StudentDetails>();
  /**
   * The title of the student details component.
   * This is set by calling the input function with the string 'Student details'.
   */
  readonly title = 'Student details';

  /**
   * A boolean flag to control the visibility of the search input.
   *
   * @type {boolean}
   */
  readonly showSearch = input(true);
  /**
   * Observable that holds the list of student items resulting from a search.
   */
  protected readonly resultStudents$: BehaviorSubject<StudentItem[]> = new BehaviorSubject<StudentItem[]>([]);
  /**
   * Observable that indicates whether the student data is being loaded.
   */
  protected readonly loadingStudent$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /**
   * Subject that emits the search term for searching students.
   */
  protected readonly searchTerm$ = new Subject<string>();

  /**
   * The current student item being viewed or edited.
   */
  protected curentStudentItem?: StudentItem;

  /**
   * Signal indicating whether the email is being checked.
   */
  protected readonly isCheckingEmail = signal(false);

  /**
   * Signal indicating whether a new student is being created.
   */
  protected readonly creatingNew = signal(true);

  /**
   * The form group for user details.
   */
  protected userDetailForm?: FormGroup;

  /**
   * Signal indicating whether the focus is on the full name field.
   */
  protected readonly focusOnFullname = signal(false);

  /**
   * Signal indicating whether the loader for user data should be shown.
   */
  protected readonly showLoaderUser = signal(false);

  /**
   * Signal indicating whether the component is in register mode.
   */
  protected isRegisterMode = signal(false);

  /**
   * Service for detecting device information.
   */
  protected deviceDetectService: DeviceDetectService = inject(DeviceDetectService);

  /**
   * The dropdown component for student search.
   */
  private studentSearcher?: NgbDropdown;

  /**
   * Subscription for checking email.
   */
  private checkingEmailSubscribe: any;

  /**
   * Service for various tools and utilities.
   */
  private tools: ToolsService = inject(ToolsService);

  /**
   * Service for displaying notifications.
   */
  private notificationsService: NotificationsService = inject(NotificationsService);

  /**
   * Service for translating text.
   */
  private translate: TranslateService = inject(TranslateService);
  private modalService: ModalManagerService = inject(ModalManagerService);
  private router: Router = inject(Router);

  /**
   * Flag indicating whether the form is being validated.
   */
  private isValidating = false;

  constructor(
    public i18nService: I18nService,
    public toolsService: ToolsService,
    private viewService: ViewService,
    private formBuilder: FormBuilder,
    private route: ActivatedRoute
  ) {
    this.initSearchUser();
    this.buildForm();
    const uid = this.route.snapshot.queryParamMap.get('uid');
    if (uid) {
      this.searchStudentForTerm(uid).subscribe((data) => {
        const student = data.find((u) => u.uid === uid);
        this.setCreatingNew(false);
        this.userDetailForm?.get('fullName')?.setValue(this.converStudentToItemFilter(student), { emitEvent: false });
      });
    }
  }

  /**
   * Sets the register mode for the component.
   * When set to `true`, the component is in register mode.
   */
  @Input() set registerMode(value: boolean) {
    this.isRegisterMode.set(value);
    this.setCreatingNew(true);
  }

  /**
   * Sets the user details for the component.
   * If the details are undefined or the student ID is not present, it loads the data.
   * If the current student item matches the student ID, it returns early.
   * Otherwise, it searches for the student and sets the student details.
   */
  @Input() set userDetails(details: StudentDetails | undefined) {
    if (!details?.student_id) {
      this.loadData(details);
      return;
    }
    if (this.curentStudentItem?.uid === details.student_id.toString()) {
      return;
    }
    this.showLoaderUser.set(true);
    this.searchStudents(details.student_id.toString()).subscribe({
      next: (students) => {
        this.showLoaderUser.set(false);
        if (students.length) {
          const student = students.filter(
            (studentToFilter) => studentToFilter.id === details.student_id?.toString()
          )[0];
          this.setCreatingNew(false);
          this.setStudent(student?.objectValues, false);
          this.userDetailForm?.get('fullName')?.setValue(student, { emitEvent: false });
        }
      },
      error: (err) => {
        this.showLoaderUser.set(false);
        console.log(err);
        this.notificationsService.notifyError(this.translate.instant('There was an error loading the student info'));
      },
    });
  }

  /**
   * Sets the student searcher dropdown component.
   */
  @ViewChild('studentSearcher', { read: NgbDropdown }) set studentSearcherBox(searcher: NgbDropdown) {
    this.studentSearcher = searcher;
  }

  /**
   * Encodes the given value as HTML entities.
   *
   * @param value The value to encode.
   * @returns The encoded HTML string.
   */
  getEntitiesHtml(value: any) {
    return this.toolsService.encodeHTMLEntities(value);
  }

  /**
   * Builds the form for user details and sets up value change subscriptions.
   */
  buildForm() {
    this.userDetailForm = this.formBuilder.group({
      student_id: [],
      fullName: [],
      student_firstname: [''],
      student_surname: [''],
      student_email: [''],
      confirm_email: [''],
      student_country: [],
      preferred_language: [],
    });
    this.setCreatingNew(true);
    this.userDetailForm.valueChanges.subscribe(() => {
      if (this.creatingNew()) {
        this.emitNewValue();
      }
    });
    this.userDetailForm.get('student_email')?.valueChanges.subscribe((value) => {
      if (!this.creatingNew() || this.isValidating) {
        return;
      }
      if (this.checkingEmailSubscribe) {
        this.checkingEmailSubscribe.unsubscribe();
        this.isCheckingEmail.set(false);
      }
      if (!this.userDetailForm?.get('student_email')?.valid) {
        return;
      }
      console.log('here');
      this.isCheckingEmail.set(true);
      this.checkingEmailSubscribe = this.viewService
        .get('/api/v1/student-registeration/users-by-email', { filters: { mail: value } })
        .subscribe({
          next: (data) => {
            this.isCheckingEmail.set(false);
            if (!data.length) {
              return;
            }
            if (this.isRegisterMode()) {
              this.modalService
                .openConfirmModal(
                  this.translate.instant('already_registered_user_by_email_title'),
                  this.translate.instant('already_registered_user_by_email', { email: value })
                )
                .subscribe((result: ConfirmModalResult) => {
                  if (result === ConfirmModalResult.yes) {
                    this.router.navigate(['/students/certification-form'], {
                      queryParams: { student_id: data[0].uid },
                    });
                  }
                });
              // this.notificationsService
              //   .errorModal(this.translate.instant('already_registered_user_by_email', { email: value }))
              //   .subscribe(() => {
              //     this.userDetailForm?.get('student_email')?.setValue('', { emitEvent: false });
              //   });
              return;
            }
            this.userDetailForm?.get('fullName')?.setValue(this.converStudentToItemFilter(data[0]));
            this.setCreatingNew(false);
          },
          error: () => {
            this.isCheckingEmail.set(false);
            this.userDetailForm?.get('student_email')?.reset();
          },
        });
    });
    this.userDetailForm.get('fullName')?.valueChanges.subscribe((value) => {
      this.setStudent(value?.objectValues, false);
    });
  }

  /**
   * Sets the flag indicating whether a new student is being created.
   * Updates the form validators based on the flag.
   *
   * @param creatingNew The flag indicating whether a new student is being created.
   */
  setCreatingNew(creatingNew: boolean): void {
    this.creatingNew.set(creatingNew);
    if (this.creatingNew()) {
      this.userDetailForm?.get('fullName')?.clearValidators();
      this.userDetailForm?.get('student_firstname')?.addValidators(Validators.required);
      this.userDetailForm?.get('student_surname')?.addValidators(Validators.required);
      this.userDetailForm?.get('student_email')?.addValidators([Validators.required, CustomValidators.email]);
      this.userDetailForm?.get('confirm_email')?.addValidators([CustomValidators.confirmEmail('student_email')]);
      if (!this.isRegisterMode()) {
        this.userDetailForm?.get('student_country')?.addValidators(Validators.required);
      } else {
        this.userDetailForm?.get('student_country')?.clearValidators();
      }
      this.userDetailForm?.get('preferred_language')?.addValidators(Validators.required);
    } else {
      this.userDetailForm?.get('fullName')?.addValidators(Validators.required);
      this.userDetailForm?.get('student_firstname')?.clearValidators();
      this.userDetailForm?.get('student_surname')?.clearValidators();
      this.userDetailForm?.get('student_email')?.clearValidators();
      this.userDetailForm?.get('confirm_email')?.clearValidators();
      this.userDetailForm?.get('student_country')?.clearValidators();
      this.userDetailForm?.get('preferred_language')?.clearValidators();
    }
  }

  /**
   * Validates the form and marks all fields as dirty.
   * Emits the new form values.
   *
   * @returns `true` if the form is valid, otherwise `false`.
   */
  validate(): boolean | any {
    this.isValidating = true;
    this.toolsService.markFormFieldsDirty(this.userDetailForm as any);
    this.emitNewValue();
    this.isValidating = false;
    return this.userDetailForm?.valid;
  }

  /**
   * Sets the preferred language in the form.
   *
   * @param language The language code to set.
   */
  setLanguage(language: string) {
    const control = this.userDetailForm?.get('preferred_language');
    control?.setValue(language);
  }

  /**
   * Searches for students based on the provided search term.
   *
   * @param searchedText The search term to use for searching students.
   * @returns An observable that emits the list of students matching the search term.
   */
  protected searchStudents = (searchedText: string): Observable<AutocompleteForFiltersItem[]> =>
    this.searchStudentForTerm(searchedText).pipe(
      map((students) => students.map((student) => this.converStudentToItemFilter(student)))
    );

  /**
   * Sets the current student from an existing one.
   *
   * @param student The student to set.
   * @param validate If the form should be validated after setting the student. Default true.
   */
  protected setStudent(student?: StudentItem, validate = true) {
    if (!student && !this.creatingNew) {
      this.clearStudent();
    } else {
      if (student) {
        this.curentStudentItem = student;
        this.userDetailForm?.get('student_id')?.setValue(student?.uid);
        this.userDetailForm?.get('student_firstname')?.setValue(student?.field_first_name);
        this.userDetailForm?.get('student_surname')?.setValue(student?.field_surname);
        this.userDetailForm?.get('student_email')?.setValue(student?.mail);
        this.userDetailForm?.get('student_country')?.setValue(student?.field_address_country_code);
        this.userDetailForm?.get('preferred_language')?.setValue(student?.preferred_langcode?.code);
      }
    }
    if (validate) {
      this.validate();
    }
  }

  private searchStudentForTerm(term: string): Observable<any[]> {
    return this.viewService.get('/api/v1/student-registeration/users', {
      filters: { search: term },
      pagination: { items_per_page: 50, page: 0 },
    });
  }

  /**
   * Emits the new values of the form.
   */
  private emitNewValue(): void {
    const values = { ...this.userDetailForm?.value };
    if (this.creatingNew()) {
      delete values.student_id;
      delete values.fullName;
      return this.userDetailsChange.emit(values);
    }
    if (values.fullName) {
      values.fullName =
        values.fullName?.originalObject?.field_first_name + ' ' + values.fullName?.originalObject?.field_surname;
    }
    return this.userDetailsChange.emit(values);
  }

  /**
   * Clears the student values and sets the current student to null.
   */
  private clearStudent() {
    this.curentStudentItem = undefined;
    this.userDetailForm?.get('student_id')?.reset();
    this.userDetailForm?.get('student_firstname')?.reset();
    this.userDetailForm?.get('student_surname')?.reset();
    this.userDetailForm?.get('student_email')?.reset();
    this.userDetailForm?.get('student_country')?.reset();
  }

  /**
   * Initializes the search user functionality.
   */
  private initSearchUser() {
    this.searchTerm$
      .asObservable()
      .pipe(
        tap((value) => {
          this.curentStudentItem = undefined;
          this.loadingStudent$.next(true);
          if (value !== '') {
            this.studentSearcher?.open();
          } else {
            this.studentSearcher?.close();
          }
        }),
        debounceTime(400),
        switchMap((term) => this.searchStudentForTerm(term)),
        tap(() => this.loadingStudent$.next(false))
      )
      .subscribe((list) => {
        this.resultStudents$.next(list);
      });
  }

  /**
   * Converts a student item to an autocomplete filter item.
   *
   * @param student The student item to convert.
   * @returns The converted autocomplete filter item.
   */
  private converStudentToItemFilter(student: StudentItem): AutocompleteForFiltersItem {
    return {
      id: student.uid,
      label: student.field_first_name
        ? `${this.tools.encodeHTMLEntities(
            this.tools.encodeHTMLEntities(student.field_first_name)
          )} ${this.tools.encodeHTMLEntities(this.tools.encodeHTMLEntities(student.field_surname))}`
        : `✉️ ${student.mail}`,
      objectValues: student,
      picture: student.user_picture_multiple_json.square_sd,
    };
  }

  /**
   * Loads the data into the form based on the provided details.
   *
   * @param details The student details to load.
   */
  private loadData(details: StudentDetails | undefined) {
    if (!details) {
      return;
    }
    this.userDetailForm?.patchValue(details, { emitEvent: false });
  }
}
